import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useEffect } from "react";
import { useDebounce } from "use-debounce";
import { formatSummary } from "~/api/chat/chat";
import { useGetOrganization } from "~/api/organizations/organizations";
import { useChatDocumentParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { useSelectedEntityStore } from "../stores/selectedEntityStore";

const DEBOUNCE_DELAY = 250;

interface FormatSummaryResponse {
  formattedContent: string;
}

interface UseFormatSummaryResult {
  format: (content: string, instructions: string) => void;
  canFormat: boolean;
  isFormatting: boolean;
  formatError: string | null;
  /**
   * The debounced entity the mutation actually targets. Exposed so the caller
   * can pass matching props (e.g. analytics `sectionId`) to the Format control,
   * avoiding drift with the live (un-debounced) store value during navigation.
   */
  debouncedEntity: { entityType: "page" | "section"; entityId: string } | null;
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "object" && data !== null) {
      const errorMsg = (data as Record<string, unknown>).error;
      const message = (data as Record<string, unknown>).message;
      if (typeof errorMsg === "string" && errorMsg) return errorMsg;
      if (typeof message === "string" && message) return message;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Failed to format summary";
}

/**
 * Hook for AI-formatting ("Tidy up") the current Summary tab content.
 *
 * Writes the formatted markdown back to the same React Query cache key the
 * summary Query / Rewrite mutation uses (`selectedSectionSummary` for the
 * currently-debounced entity). The query key is captured at mutate() time so
 * the result is always written to the entity that was formatted, even if the
 * user navigates to a different section mid-flight — same pattern as Rewrite.
 */
export const useFormatSummary = (): UseFormatSummaryResult => {
  const { organizationId, caseId: projectId } = useChatDocumentParams();
  const { data: organization } = useGetOrganization(organizationId);
  const selectedEntity = useSelectedEntityStore(
    (state) => state.selectedEntity
  );

  const [debouncedSelectedEntity] = useDebounce(selectedEntity, DEBOUNCE_DELAY);
  const queryClient = useQueryClient();

  const queryKey = [
    "selectedSectionSummary",
    {
      orgId: organizationId,
      projectId,
      entityType: debouncedSelectedEntity?.entityType,
      entityId: debouncedSelectedEntity?.entityId,
      pageIds: debouncedSelectedEntity?.pageIds.join(",") || "",
      language: organization?.language,
    },
  ];

  interface FormatVariables {
    queryKey: readonly unknown[];
    entityType: "page" | "section";
    entityId: string;
    content: string;
    instructions: string;
  }

  const {
    mutate: formatMutate,
    isPending: isFormatting,
    isError,
    error: mutationError,
    reset: resetMutation,
  } = useMutation<FormatSummaryResponse, Error, FormatVariables>({
    mutationFn: async ({ entityType, entityId, content, instructions }) => {
      logger.info(
        `Formatting summary for ${entityType} ${entityId} (instructions length: ${instructions.length})`
      );

      try {
        return await formatSummary(organizationId, projectId, {
          entityType,
          entityId,
          content,
          instructions,
        });
      } catch (error: unknown) {
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        logger.error(`Failed to format summary for ${entityType} ${entityId}`, {
          status,
          entityType,
          entityId,
        });
        throw new Error(getErrorMessage(error));
      }
    },
    // Write the formatted content back to the exact cache entry the Summary
    // query reads from, mirroring the summary response shape so the in-place
    // update works without a refetch.
    onSuccess: (result, variables) => {
      queryClient.setQueryData(variables.queryKey, {
        summary: result.formattedContent,
        truncated: false,
        cached: false,
      });
      trackEvent({
        eventName: "app/doc_sidebar/summary/format.success",
        properties: {
          entityType: variables.entityType,
          entityId: variables.entityId,
          instructionsLength: variables.instructions.length,
        },
      });
    },
    onError: (error, variables) => {
      trackEvent({
        eventName: "app/doc_sidebar/summary/format.error",
        properties: {
          entityType: variables.entityType,
          entityId: variables.entityId,
          error: error.message,
        },
      });
    },
  });

  // Clear any prior mutation error when the selected entity changes so a
  // failed format on section A never bleeds into section B's UI.
  useEffect(() => {
    resetMutation();
  }, [
    debouncedSelectedEntity?.entityType,
    debouncedSelectedEntity?.entityId,
    resetMutation,
  ]);

  const format = (content: string, instructions: string) => {
    if (!debouncedSelectedEntity?.entityId) return;
    formatMutate({
      queryKey,
      entityType: debouncedSelectedEntity.entityType,
      entityId: debouncedSelectedEntity.entityId,
      content,
      instructions,
    });
  };

  const canFormat = !!debouncedSelectedEntity?.entityId && !isFormatting;
  const formatError = isError
    ? mutationError?.message || "Failed to format summary"
    : null;

  const debouncedEntity = debouncedSelectedEntity
    ? {
        entityType: debouncedSelectedEntity.entityType,
        entityId: debouncedSelectedEntity.entityId,
      }
    : null;

  return { format, canFormat, isFormatting, formatError, debouncedEntity };
};
